import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { IntroSection } from '../components/IntroSection';
import { SlideContainer } from '../components/SlideContainer';
import { PollQuestion } from '../components/PollQuestion';
import { QuizQuestion } from '../components/QuizQuestion';
import { RankingQuestion } from '../components/RankingQuestion';
import { ProgressBar } from '../components/ProgressBar';
export function VabysmoJourney() {
  const [currentSection, setCurrentSection] = useState(0);
  const [showConsequences, setShowConsequences] = useState<number[]>([]);
  const handleBegin = () => {
    setCurrentSection(1);
    const element = document.getElementById('slide-1');
    element?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const triggerConfetti = () => {
    confetti({
      particleCount: 100,
      spread: 70,
      origin: {
        y: 0.6
      },
      colors: ['#2ec4b6', '#0d1b2a', '#10b981']
    });
  };
  useEffect(() => {
    if (currentSection === 2) {
      const timers = [0, 200, 400, 600].map((delay, index) => setTimeout(() => setShowConsequences(prev => [...prev, index]), delay));
      return () => timers.forEach(clearTimeout);
    }
  }, [currentSection]);
  useEffect(() => {
    if (currentSection === 7) {
      triggerConfetti();
    }
  }, [currentSection]);
  const getProgress = () => {
    if (currentSection === 0) return 0;
    if (currentSection === 1) return 14;
    if (currentSection === 2) return 28;
    if (currentSection === 3) return 42;
    if (currentSection === 4) return 57;
    if (currentSection === 5) return 71;
    if (currentSection === 6) return 85;
    return 100;
  };
  return <div className="bg-gray-50" data-id="element-118">
      {currentSection === 0 && <IntroSection onBegin={handleBegin} data-id="element-119" />}

      {/* Slide 1: The Challenge */}
      <div id="slide-1" data-id="element-120">
        <SlideContainer sectionTag="VILLAIN" backgroundColor="bg-gradient-to-b from-[#0d1b2a] to-[#1a2332]" psychologyTags={['🧠 Human First', '🎯 Micro-Commitment', '⚡ Contrast']} data-id="element-121">
          <h1 className="text-4xl font-bold text-white leading-tight" data-id="element-122">
            When every treatment looks the same, patients pay the price.
          </h1>

          <div className="text-center py-8" data-id="element-123">
            <div className="text-7xl font-bold text-[#2ec4b6]" data-id="element-124">72%</div>
            <p className="text-lg text-gray-300 mt-2" data-id="element-125">
              of clinicians report limited differentiation between current
              treatment options
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6" data-id="element-126">
            <PollQuestion question="Does this challenge affect your practice?" options={['Yes', 'Sometimes', 'Rarely']} results={[{
            label: 'Yes',
            percentage: 64
          }, {
            label: 'Sometimes',
            percentage: 28
          }, {
            label: 'Rarely',
            percentage: 8
          }]} data-id="element-127" />
          </div>
        </SlideContainer>
      </div>

      {/* Slide 2: The Cost */}
      <SlideContainer sectionTag="VILLAIN" backgroundColor="bg-white" psychologyTags={['💔 Loss Awareness', '⚡ Contrast', '🔍 Clarity']} data-id="element-128">
        <h2 className="text-3xl font-bold text-gray-900" data-id="element-129">
          The Cost of the Status Quo
        </h2>

        <div className="space-y-4" data-id="element-130">
          {[{
          icon: '🔴',
          text: 'Suboptimal patient outcomes'
        }, {
          icon: '🟠',
          text: 'Frequent treatment switches'
        }, {
          icon: '🟠',
          text: 'Eroding clinician confidence'
        }, {
          icon: '🔴',
          text: 'Growing clinical uncertainty'
        }].map((item, index) => <motion.div key={index} initial={{
          opacity: 0,
          x: -20
        }} animate={showConsequences.includes(index) ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.4
        }} className="flex items-center gap-4 p-4 bg-red-50 border border-red-200 rounded-lg" data-id="element-131">
              <span className="text-3xl" data-id="element-132">{item.icon}</span>
              <span className="text-lg font-semibold text-gray-900" data-id="element-133">
                {item.text}
              </span>
            </motion.div>)}
        </div>

        {showConsequences.length === 4 && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="mt-8 p-6 bg-green-50 border-2 border-green-500 rounded-lg" data-id="element-134">
            <p className="text-xl font-semibold text-green-900 text-center" data-id="element-135">
              But what if there was a fundamentally different approach?
            </p>
          </motion.div>}
      </SlideContainer>

      {/* Slide 3: Hero Reveal */}
      <SlideContainer sectionTag="HERO" backgroundColor="bg-white" psychologyTags={['⚡ Contrast', '🔍 Clarity', '💎 Incentive']} data-id="element-136">
        <div className="text-center space-y-8 py-12" data-id="element-137">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-138">
            The first and only bispecific antibody
          </h2>

          <div className="text-6xl font-bold text-[#2ec4b6]" data-id="element-139">Vabysmo</div>

          <div className="flex justify-center items-center gap-8 py-8" data-id="element-140">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center" data-id="element-141">
              <div className="w-12 h-12 bg-teal-500 rounded-full" data-id="element-142" />
            </div>
            <div className="flex flex-col gap-2" data-id="element-143">
              <div className="w-24 h-1 bg-teal-500" data-id="element-144" />
              <div className="w-24 h-1 bg-teal-500" data-id="element-145" />
            </div>
            <div className="flex gap-2" data-id="element-146">
              <div className="w-8 h-16 bg-teal-300 rounded" data-id="element-147" />
              <div className="w-8 h-16 bg-teal-300 rounded" data-id="element-148" />
            </div>
          </div>

          <button className="px-8 py-4 bg-[#2ec4b6] text-white font-semibold rounded-lg shadow-md hover:bg-[#26a89c] transition-colors" data-id="element-149">
            See how it works →
          </button>
        </div>
      </SlideContainer>

      {/* Slide 4: Mechanism */}
      <SlideContainer sectionTag="HERO" backgroundColor="bg-gray-50" psychologyTags={['🏛️ Authority', '⚡ Contrast', '🔍 Clarity']} data-id="element-150">
        <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-151">
          How It Works Differently
        </h2>

        <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-152">
          <div className="p-6 bg-gray-200 rounded-lg" data-id="element-153">
            <h3 className="font-bold text-gray-700 mb-4" data-id="element-154">Standard Approach</h3>
            <div className="w-full h-32 bg-gray-400 rounded mb-3 flex items-center justify-center" data-id="element-155">
              <div className="w-16 h-16 bg-gray-600 rounded-full" data-id="element-156" />
            </div>
            <p className="text-sm text-gray-600" data-id="element-157">One target: VEGF-A only</p>
          </div>

          <div className="p-6 bg-teal-50 border-2 border-teal-500 rounded-lg" data-id="element-158">
            <h3 className="font-bold text-teal-900 mb-4" data-id="element-159">Vabysmo Approach</h3>
            <div className="w-full h-32 bg-teal-200 rounded mb-3 flex items-center justify-center gap-4" data-id="element-160">
              <div className="w-12 h-12 bg-teal-600 rounded-full" data-id="element-161" />
              <div className="w-12 h-12 bg-teal-600 rounded-full" data-id="element-162" />
            </div>
            <p className="text-sm text-teal-700 font-semibold" data-id="element-163">
              Two targets: VEGF-A + Ang-2
            </p>
          </div>
        </div>

        <QuizQuestion question="What makes Vabysmo's mechanism unique?" options={[{
        label: 'A',
        text: 'Dual pathway inhibition',
        isCorrect: true
      }, {
        label: 'B',
        text: 'Higher dosing frequency',
        isCorrect: false
      }, {
        label: 'C',
        text: 'Faster absorption rate',
        isCorrect: false
      }]} explanation="Correct! Vabysmo's bispecific design allows it to inhibit both VEGF-A and Ang-2 pathways simultaneously, providing a fundamentally different mechanism of action." data-id="element-164" />
      </SlideContainer>

      {/* Slide 5: Proof */}
      <SlideContainer sectionTag="PROOF" backgroundColor="bg-white" psychologyTags={['🏛️ Authority', '👥 Peer', '💎 Incentive']} data-id="element-165">
        <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-166">
          What This Means for Patients
        </h2>

        <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 mb-6" data-id="element-167">
          <div className="text-center" data-id="element-168">
            <div className="text-5xl font-bold text-teal-700 mb-2" data-id="element-169">
              Extended
            </div>
            <p className="text-lg text-gray-700" data-id="element-170">
              Treatment interval capability
            </p>
          </div>
        </div>

        <div className="space-y-3 mb-6" data-id="element-171">
          <div className="flex items-end gap-2" data-id="element-172">
            <div className="flex-1 bg-gray-300 h-24 rounded flex items-end justify-center pb-2" data-id="element-173">
              <span className="text-sm font-semibold text-gray-700" data-id="element-174">
                Standard
              </span>
            </div>
            <div className="flex-1 bg-teal-500 h-40 rounded flex items-end justify-center pb-2" data-id="element-175">
              <span className="text-sm font-semibold text-white" data-id="element-176">Vabysmo</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 text-center" data-id="element-177">
            Efficacy comparison in clinical trials
          </p>
        </div>

        <PollQuestion question="Would this evidence change your treatment approach?" options={['Yes', 'Possibly', 'Need more data']} results={[{
        label: 'Yes',
        percentage: 52
      }, {
        label: 'Possibly',
        percentage: 38
      }, {
        label: 'Need more data',
        percentage: 10
      }]} data-id="element-178" />
      </SlideContainer>

      {/* Slide 6: Peer Proof */}
      <SlideContainer sectionTag="PROOF" backgroundColor="bg-[#faf8f5]" psychologyTags={['👥 Peer', '🏛️ Authority', '🔄 Loop']} data-id="element-179">
        <div className="text-center mb-8" data-id="element-180">
          <div className="text-6xl font-bold text-gray-900 mb-4" data-id="element-181">83%</div>
          <p className="text-xl text-gray-700 leading-relaxed" data-id="element-182">
            of ophthalmologists surveyed recognize the value of dual-pathway
            inhibition
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8" data-id="element-183">
          <p className="text-gray-700 italic mb-2" data-id="element-184">
            "The bispecific approach represents a meaningful advancement in our
            treatment options."
          </p>
          <p className="text-sm text-gray-500" data-id="element-185">— Leading KOL, Ophthalmology</p>
        </div>

        <RankingQuestion question="Rank what matters most to you in a new treatment:" items={['Efficacy', 'Safety', 'Convenience', 'Mechanism novelty', 'Cost']} data-id="element-186" />
      </SlideContainer>

      {/* Slide 7: CTA */}
      <SlideContainer sectionTag="ACTION" backgroundColor="bg-white" psychologyTags={['💔 Loss Awareness', '💎 Incentive', '🎯 Micro-Commitment']} data-id="element-187">
        <div className="text-center space-y-8" data-id="element-188">
          <div data-id="element-189">
            <div className="text-5xl mb-4" data-id="element-190">✨</div>
            <h2 className="text-3xl font-bold text-gray-900" data-id="element-191">
              You've completed this Snak™
            </h2>
          </div>

          <button className="w-full py-4 bg-[#2ec4b6] text-white font-bold text-lg rounded-lg shadow-md hover:bg-[#26a89c] transition-colors" data-id="element-192">
            Book a Consultation
          </button>

          <div className="space-y-3" data-id="element-193">
            <label htmlFor="questions" className="block text-left text-sm font-semibold text-gray-700" data-id="element-194">
              Any questions or topics you'd like to discuss?
            </label>
            <textarea id="questions" rows={4} placeholder="Share your thoughts..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent" data-id="element-195" />
          </div>

          <button className="text-teal-600 font-semibold hover:text-teal-700 transition-colors" data-id="element-196">
            Share with a colleague →
          </button>
        </div>
      </SlideContainer>

      {currentSection > 0 && <ProgressBar progress={getProgress()} data-id="element-197" />}
    </div>;
}